import { ChatState, StreamingState, StreamingRefsFull } from './types';

interface TokenTickerState {
  refs: StreamingRefsFull;
  streaming: StreamingState;
  chat: ChatState;
}

const clearStreamingUI = (streaming: StreamingState) => {
  streaming.setStreamingDisplay('');
  streaming.setIsAiTyping(false);
  streaming.setCurrentStreamingMsgId(null);
};

const clearStreamingRefs = (refs: StreamingRefsFull) => {
  refs.clearTokenInterval();
  refs.clearTokenQueue();
  refs.setPendingCompletion(null);
  refs.setTitleGenerationBuffer('');
  refs.setStreamingBuffer('');
  refs.setStreamController(null);
  refs.setStreamingMessageId(null);
};

export const resetStreamingState = (refs: StreamingRefsFull, streaming: StreamingState, chat?: ChatState) => {
  clearStreamingRefs(refs);
  clearStreamingUI(streaming);
  resetRunwayTracker();
  if (chat) chat.setIsLoading(false);
};

/** Bind the temporary live surface to its durable assistant message. This is
 * deliberately independent from visual finalization: project hydration can
 * return the saved message while the local word ticker is still draining. */
export const bindStreamingMessageId = (
  refs: StreamingRefsFull,
  streaming: StreamingState,
  messageId: string,
) => {
  if (!messageId || refs.streamingMessageIdRef.current === messageId) return;
  refs.setStreamingMessageId(messageId);
  streaming.setCurrentStreamingMsgId(messageId);
};

const handleEmptyQueue = (
  refs: StreamingRefsFull, 
  streaming: StreamingState, 
  chat: ChatState, 
  stopTicker: () => void
) => {
  if (refs.pendingCompletionRef.current) {
    stopTicker();
    refs.pendingCompletionRef.current.finalizeCallback();
    refs.setPendingCompletion(null);
  } else if (!refs.streamControllerRef.current) {
    // If stream is done and no pending completion, just stop & reset
    stopTicker();
    resetStreamingState(refs, streaming, chat);
  }
  // If stream is still active, we just wait (Keep Alive)
};

/** Approximate height of one line of chat text (font 16 × 1.6 + p margins). */
const CHAT_LINE_H = 30;
/** Pre-allocate this many lines of "buffer" before scrolling again. */
const CHAT_RUNWAY_LINES = 3;
const CHAT_RUNWAY_PX = CHAT_LINE_H * CHAT_RUNWAY_LINES;

/**
 * Height-tracking state for the runway scroll throttle.
 * Shared across token ticks within a single streaming session.
 */
let runwayBaseline = 0;
let runwayInitialized = false;

/** Reset the runway tracker (call when streaming starts/stops). */
export const resetRunwayTracker = (): void => { runwayInitialized = false; };

/** Scroll the messagesEnd ref into view and update the height baseline. */
const scrollAndRebaseline = (state: TokenTickerState, h: number) => {
  runwayBaseline = h;
  state.refs.messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
};

/** Scroll if content has grown past the runway buffer (≈3 lines). */
const maybeRunwayScroll = (state: TokenTickerState) => {
  const container = state.refs.chatContainerRef.current;

  if (!container) return;

  const h = container.scrollHeight;

  if (!runwayInitialized) { runwayInitialized = true; scrollAndRebaseline(state, h); }
  else if (h - runwayBaseline >= CHAT_RUNWAY_PX) scrollAndRebaseline(state, h);
};

const processToken = (token: string, state: TokenTickerState) => {
  state.streaming.appendStreamingDisplay(token);

  if (state.refs.shouldAutoScrollRef.current) maybeRunwayScroll(state);
};
  
export const processTickerQueue = (state: TokenTickerState, stopTicker: () => void) => {
  const next = state.refs.shiftToken();

  if (next === undefined) handleEmptyQueue(state.refs, state.streaming, state.chat, stopTicker);
  else processToken(next, state);
};

export const handleStreamError = (
  error: string,
  chat: ChatState,
  streaming: StreamingState,
  refs: StreamingRefsFull,
  resetDisplayFn: () => void,
  tempMessageId?: string
) => {
  chat.setError(error);
  resetDisplayFn();
  // HTTP and network failures need not deliver onStreamEnd. End the waiting
  // state here as well, without changing the saved transcript or retry error.
  resetStreamingState(refs, streaming, chat);
  refs.setIsUserStreaming(false);
  if (tempMessageId) chat.setMessages((prev) => prev.filter((m) => m.id !== tempMessageId));
};
